import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../../../contexts/TenantContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Step,
  StepLabel,
  StepConnector,
  Stepper,
  TextField,
  Typography,
  LinearProgress,
  Snackbar,
  Backdrop,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  Tooltip,
  stepConnectorClasses,
  styled,
  Breadcrumbs,
  Link,
  Container
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  AccessTime as AccessTimeIcon,
  MonetizationOn as MonetizationOnIcon,
  Comment as CommentIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cached as CachedIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  ReceiptLong as ReceiptLongIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon
} from '@mui/icons-material';
import financialRequestService from '../../../services/financialRequestService';

// Styled components for custom stepper
const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300],
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
  display: 'flex',
  height: 22,
  alignItems: 'center',
  ...(ownerState.active && {
    color: theme.palette.primary.main,
  }),
  '& .QontoStepIcon-completedIcon': {
    color: theme.palette.primary.main,
    zIndex: 1,
    fontSize: 18,
  },
  '& .QontoStepIcon-circle': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckIcon className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'Requested':
      return 'info';
    case 'Review':
      return 'warning';
    case 'Approved':
      return 'success';
    case 'Declined':
      return 'error';
    case 'MemberRejected':
      return 'error';
    case 'Disbursed':
      return 'success';
    case 'Completed':
      return 'success';
    default:
      return 'default';
  }
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// SLA configuration for each status (in hours)
const SLA_CONFIG = {
  'Requested': 24, // 24 hours to move from Requested to Review
  'Review': 48,    // 48 hours to move from Review to Approved/Declined
  'Approved': 72,  // 72 hours to disburse funds after approval
  'Disbursed': 72  // 72 hours for member to confirm receipt
};

// Helper function to calculate SLA status
const getSlaStatus = (status, startDate) => {
  if (!status || !startDate || !SLA_CONFIG[status]) return 'none';
  
  const now = new Date();
  const statusDate = new Date(startDate);
  const elapsedHours = (now - statusDate) / (1000 * 60 * 60);
  const slaLimit = SLA_CONFIG[status];
  
  // Calculate SLA threshold percentages
  const warningThreshold = 0.7; // 70% of SLA time used = amber
  const criticalThreshold = 1.0; // 100% of SLA time used = red
  
  if (elapsedHours >= slaLimit * criticalThreshold) {
    return 'red'; // Exceeded SLA
  } else if (elapsedHours >= slaLimit * warningThreshold) {
    return 'amber'; // Approaching SLA limit
  } else {
    return 'green'; // Within SLA
  }
};

// Helper function to get time remaining text for SLA
const getSlaTimeRemaining = (status, startDate) => {
  if (!status || !startDate || !SLA_CONFIG[status]) return '';
  
  const now = new Date();
  const statusDate = new Date(startDate);
  const elapsedHours = (now - statusDate) / (1000 * 60 * 60);
  const slaLimit = SLA_CONFIG[status];
  
  if (elapsedHours >= slaLimit) {
    const overBy = Math.floor(elapsedHours - slaLimit);
    return `Exceeded by ${overBy} hour${overBy !== 1 ? 's' : ''}`;
  } else {
    const remaining = Math.ceil(slaLimit - elapsedHours);
    return `${remaining} hour${remaining !== 1 ? 's' : ''} remaining`;
  }
};

// Helper to get color from SLA status
const getSlaStatusColor = (theme, status) => {
  switch (status) {
    case 'red':
      return theme.palette.error.main;
    case 'amber':
      return theme.palette.warning.main;
    case 'green':
      return theme.palette.success.main;
    default:
      return theme.palette.text.secondary;
  }
};

const AdminFinancialRequestDetailPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { tenant } = useTenant();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  // State for comment
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for action dialogs
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [openDisburseDialog, setOpenDisburseDialog] = useState(false);
  const [actionComment, setActionComment] = useState('');
  const [disburseAmount, setDisburseAmount] = useState('');

  // Add new state variables for improved feedback
  const [processing, setProcessing] = useState(false);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [optimisticStatus, setOptimisticStatus] = useState(null);
  const [processingAction, setProcessingAction] = useState('');

  // Steps for the stepper
  const steps = ['Requested', 'In Review', 'Approved', 'Disbursed', 'Completed'];

  // Map request status to stepper step
  const getStepFromStatus = (status) => {
    switch (status) {
      case 'Requested':
        return 0;
      case 'Review':
        return 1;
      case 'Approved':
      case 'MemberRejected':
      case 'Declined':
        return 2;
      case 'Disbursed':
        return 3;
      case 'Completed':
        return 4;
      default:
        return 0;
    }
  };

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        // Get current tenant ID from context or localStorage
        const currentTenantId = tenant?._id || localStorage.getItem('tenantId');
        
        if (!currentTenantId) {
          console.warn('No tenant ID found when fetching request details');
        } else {
          console.log(`Fetching request with ID ${requestId} for tenant ${currentTenantId}`);
        }
        
        const response = await financialRequestService.getRequestById(requestId, currentTenantId);
        setRequest(response.data);
        setActiveStep(getStepFromStatus(response.data.status));

        // Initialize disburse amount if available
        if (response.data.amount > 0) {
          setDisburseAmount(response.data.amount.toString());
        }
      } catch (err) {
        console.error('Error fetching request details:', err);
        setError(err.message || 'Error loading the request details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequest();
    }
  }, [requestId, tenant]);

  // Function to handle optimistic UI updates
  const updateStatusOptimistically = (newStatus) => {
    setOptimisticStatus(newStatus);
    setActiveStep(getStepFromStatus(newStatus));
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await financialRequestService.addComment(requestId, comment);
      setRequest(response.data);
      setComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced status update with immediate feedback
  const handleMoveToReview = async () => {
    setProcessingAction('Moving to review');
    setProcessing(true);
    setIsSubmitting(true);

    // Immediate optimistic update
    updateStatusOptimistically('Review');

    try {
      const response = await financialRequestService.updateRequestStatus(
        requestId,
        'Review',
        `Request moved to review${actionComment ? ': ' + actionComment : ''}`
      );

      // Update the actual data once the API returns
      setRequest(response.data);
      setOpenReviewDialog(false);
      setActionComment('');

      // Show success notification
      setSuccessMessage('Request successfully moved to review status');
      setStatusUpdateSuccess(true);
    } catch (err) {
      console.error('Error moving request to review:', err);
      setError('Failed to update request: ' + (err.message || 'Unknown error'));

      // Revert the optimistic update on error
      setOptimisticStatus(request.status);
      setActiveStep(getStepFromStatus(request.status));
    } finally {
      setIsSubmitting(false);
      setProcessing(false);
    }
  };

  const handleApproveRequest = async () => {
    setProcessingAction('Approving request');
    setProcessing(true);
    setIsSubmitting(true);

    // Immediate optimistic update
    updateStatusOptimistically('Approved');

    try {
      const response = await financialRequestService.updateRequestStatus(
        requestId,
        'Approved',
        `Request approved${actionComment ? ': ' + actionComment : ''}`
      );

      setRequest(response.data);
      setOpenApproveDialog(false);
      setActionComment('');

      setSuccessMessage('Request successfully approved');
      setStatusUpdateSuccess(true);
    } catch (err) {
      console.error('Error approving request:', err);
      setError('Failed to approve request: ' + (err.message || 'Unknown error'));

      // Revert on error
      setOptimisticStatus(request.status);
      setActiveStep(getStepFromStatus(request.status));
    } finally {
      setIsSubmitting(false);
      setProcessing(false);
    }
  };

  const handleDeclineRequest = async () => {
    if (!actionComment.trim()) return;

    setProcessingAction('Declining request');
    setProcessing(true);
    setIsSubmitting(true);

    // Immediate optimistic update
    updateStatusOptimistically('Declined');

    try {
      const response = await financialRequestService.updateRequestStatus(
        requestId,
        'Declined',
        `Request declined: ${actionComment}`
      );

      setRequest(response.data);
      setOpenDeclineDialog(false);
      setActionComment('');

      setSuccessMessage('Request successfully declined');
      setStatusUpdateSuccess(true);
    } catch (err) {
      console.error('Error declining request:', err);
      setError('Failed to decline request: ' + (err.message || 'Unknown error'));

      // Revert on error
      setOptimisticStatus(request.status);
      setActiveStep(getStepFromStatus(request.status));
    } finally {
      setIsSubmitting(false);
      setProcessing(false);
    }
  };

  const handleDisburseRequest = async () => {
    if (!disburseAmount || parseFloat(disburseAmount) <= 0) return;

    setProcessingAction('Disbursing funds');
    setProcessing(true);
    setIsSubmitting(true);

    // Immediate optimistic update
    updateStatusOptimistically('Disbursed');

    try {
      const amountText = `$${parseFloat(disburseAmount).toFixed(2)}`;
      const response = await financialRequestService.updateRequestStatus(
        requestId,
        'Disbursed',
        `Funds disbursed: ${amountText}${actionComment ? '. ' + actionComment : ''}`,
        parseFloat(disburseAmount)
      );

      setRequest(response.data);
      setOpenDisburseDialog(false);
      setActionComment('');

      setSuccessMessage(`$${parseFloat(disburseAmount).toFixed(2)} successfully disbursed to member`);
      setStatusUpdateSuccess(true);
    } catch (err) {
      console.error('Error disbursing funds:', err);
      setError('Failed to disburse funds: ' + (err.message || 'Unknown error'));

      // Revert on error
      setOptimisticStatus(request.status);
      setActiveStep(getStepFromStatus(request.status));
    } finally {
      setIsSubmitting(false);
      setProcessing(false);
    }
  };

  const handleViewMember = () => {
    if (request?.member?._id) {
      navigate(`/admin/members/${request.member._id}`);
    }
  };

  // Status display mapping
  const statusDisplay = {
    'Requested': 'Requested',
    'Review': 'In Review',
    'Approved': 'Approved',
    'Declined': 'Declined',
    'MemberRejected': 'Rejected by Member',
    'Disbursed': 'Funds Disbursed',
    'Completed': 'Completed'
  };

  // Current status, either optimistic or actual
  const currentStatus = optimisticStatus || (request ? request.status : '');

  // Status indicator icons
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Requested':
        return <AccessTimeIcon />;
      case 'Review':
        return <EditIcon />;
      case 'Approved':
        return <CheckCircleIcon />;
      case 'Declined':
        return <CloseIcon />;
      case 'MemberRejected':
        return <CloseIcon />;
      case 'Disbursed':
        return <AttachMoneyIcon />;
      case 'Completed':
        return <DoneAllIcon />;
      default:
        return <AccessTimeIcon />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          py: 8,
        }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading request details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.1)',
          }}
          icon={<WarningIcon fontSize="inherit" />}
        >
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/admin/financial-requests')}
          startIcon={<ArrowBackIcon />}
          sx={{ borderRadius: 1 }}
        >
          Back to Requests
        </Button>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(237, 108, 2, 0.1)',
          }}
          icon={<WarningIcon fontSize="inherit" />}
        >
          Request not found
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/admin/financial-requests')}
          startIcon={<ArrowBackIcon />}
          sx={{ borderRadius: 1 }}
        >
          Back to Requests
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        backgroundColor: alpha(theme.palette.primary.light, 0.04),
        minHeight: '100vh',
      }}>
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link 
            color="inherit" 
            href="/admin/dashboard" 
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Dashboard
          </Link>
          <Link
            color="inherit"
            href="/admin/financial-requests"
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <AccountBalanceIcon sx={{ mr: 0.5 }} fontSize="small" />
            Financial Requests
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptLongIcon sx={{ mr: 0.5 }} fontSize="small" />
            Request Details
          </Typography>
        </Breadcrumbs>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Financial Assistance Request
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {requestId} • Created: {formatDate(request.requestDate)}
            </Typography>
          </Box>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/admin/financial-requests')}
            startIcon={<ArrowBackIcon />}
            sx={{ borderRadius: 1 }}
          >
            Back to Requests
          </Button>
        </Box>

        {/* Processing indicator */}
        {processing && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress
              color="primary"
              sx={{
                height: 6,
                borderRadius: 3
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              {processingAction}...
            </Typography>
          </Box>
        )}

        <Card 
          elevation={0} 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Box 
            sx={{ 
              p: 2,
              background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              color: 'white'
            }}
          >
            <Typography variant="h6">
              Request Progress
            </Typography>
          </Box>
          <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel 
              connector={<QontoConnector />}
            >
              {steps.map((label, index) => {
                const stepProps = {};
                if (index < activeStep || currentStatus === 'Completed') {
                  stepProps.completed = true;
                }
                
                return (
                  <Step key={label} {...stepProps}>
                    <StepLabel 
                      StepIconComponent={QontoStepIcon}
                      sx={{
                        '& .MuiStepLabel-label': {
                          mt: 1,
                          fontWeight: index === activeStep ? 600 : 400,
                          color: index === activeStep ? 'primary.main' : 'text.secondary',
                        }
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card 
              elevation={0} 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardHeader 
                title="Request Details" 
                sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: getStatusColor(currentStatus),
                        boxShadow: `0 4px 8px ${alpha(theme.palette[getStatusColor(currentStatus)].main, 0.4)}`
                      }}
                    >
                      {getStatusIcon(currentStatus)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Current Status
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {statusDisplay[currentStatus] || currentStatus}
                      </Typography>
                    </Box>
                  </Box>

                  {/* SLA Status Indicator */}
                  {SLA_CONFIG[currentStatus] && (
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      border: '1px solid',
                      borderColor: (theme) => {
                        const slaStatus = getSlaStatus(currentStatus, request.lastUpdated || request.requestDate);
                        return alpha(getSlaStatusColor(theme, slaStatus), 0.3);
                      }
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          SLA Status
                        </Typography>
                        <Chip
                          size="small"
                          label={(() => {
                            const slaStatus = getSlaStatus(currentStatus, request.lastUpdated || request.requestDate);
                            switch(slaStatus) {
                              case 'green': return 'Within SLA';
                              case 'amber': return 'Approaching Limit';
                              case 'red': return 'SLA Exceeded';
                              default: return 'No SLA';
                            }
                          })()}
                          sx={{
                            bgcolor: (theme) => {
                              const slaStatus = getSlaStatus(currentStatus, request.lastUpdated || request.requestDate);
                              return alpha(getSlaStatusColor(theme, slaStatus), 0.1);
                            },
                            color: (theme) => {
                              const slaStatus = getSlaStatus(currentStatus, request.lastUpdated || request.requestDate);
                              return getSlaStatusColor(theme, slaStatus);
                            },
                            fontWeight: 'medium',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Time Remaining:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(() => {
                                if (!currentStatus || !request?.lastUpdated) return 0;
                                const slaLimit = SLA_CONFIG[currentStatus];
                                if (!slaLimit) return 0;
                                
                                const now = new Date();
                                const statusDate = new Date(request.lastUpdated || request.requestDate);
                                const elapsedHours = (now - statusDate) / (1000 * 60 * 60);
                                const percentComplete = Math.min(100, (elapsedHours / slaLimit) * 100);
                                return percentComplete;
                              })()}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: alpha(theme.palette.grey[300], 0.5),
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  bgcolor: (theme) => {
                                    const slaStatus = getSlaStatus(currentStatus, request.lastUpdated || request.requestDate);
                                    return getSlaStatusColor(theme, slaStatus);
                                  }
                                }
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 'medium',
                            color: (theme) => {
                              const slaStatus = getSlaStatus(currentStatus, request.lastUpdated || request.requestDate);
                              return getSlaStatusColor(theme, slaStatus);
                            }
                          }}>
                            {getSlaTimeRemaining(currentStatus, request.lastUpdated || request.requestDate)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          Target: {SLA_CONFIG[currentStatus]} hours for {statusDisplay[currentStatus]} stage
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Member
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          width: 36,
                          height: 36,
                          mr: 1.5
                        }}
                      >
                        {request.member?.userId?.firstName?.charAt(0) || 'U'}
                      </Avatar>
                      <Box>
                        <Button
                          color="primary"
                          onClick={handleViewMember}
                          sx={{ textTransform: 'none', fontWeight: 500, p: 0 }}
                        >
                          {request.member?.userId?.firstName} {request.member?.userId?.lastName}
                        </Button>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Member since {formatDate(request.member?.joinDate || request.requestDate).split(',')[0]}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Reason
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ 
                        p: 2, 
                        mt: 1, 
                        borderRadius: 1,
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        bgcolor: alpha(theme.palette.background.paper, 0.7)
                      }}
                    >
                      <Typography variant="body2">
                        {request.reason}
                      </Typography>
                    </Paper>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Amount Requested
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ 
                        p: 2, 
                        mt: 1, 
                        borderRadius: 1,
                        borderColor: alpha(theme.palette.success.main, 0.3),
                        bgcolor: alpha(theme.palette.success.light, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                      }}
                    >
                      <AttachMoneyIcon color="success" />
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {request.amount > 0 ? request.amount.toFixed(2) : 'Not specified'}
                      </Typography>
                    </Paper>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Timeline
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Request Created
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(request.requestDate)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CachedIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Last Updated
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(request.lastUpdated)}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    mb: 3, 
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }}
                >
                  <CardHeader 
                    title="Actions" 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                  <CardContent>
                    {currentStatus === 'Requested' && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <AccessTimeIcon color="info" sx={{ mr: 1 }} />
                          <Typography variant="h6" color="info.main">
                            New Request
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                          This request has been submitted and is awaiting your review. Please review the details and take appropriate action.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <LoadingButton
                            variant="contained"
                            color="primary"
                            onClick={() => setOpenReviewDialog(true)}
                            disabled={processing || isSubmitting}
                            loading={processing && processingAction === 'Moving to review'}
                            startIcon={<EditIcon />}
                            sx={{ 
                              borderRadius: 1,
                              boxShadow: '0 4px 10px rgba(25, 118, 210, 0.2)'
                            }}
                          >
                            Move to Review
                          </LoadingButton>
                          <LoadingButton
                            variant="outlined"
                            color="error"
                            onClick={() => setOpenDeclineDialog(true)}
                            disabled={processing || isSubmitting}
                            loading={processing && processingAction === 'Declining request'}
                            startIcon={<CloseIcon />}
                            sx={{ borderRadius: 1 }}
                          >
                            Decline
                          </LoadingButton>
                        </Box>
                      </Box>
                    )}

                    {currentStatus === 'Review' && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <EditIcon color="warning" sx={{ mr: 1 }} />
                          <Typography variant="h6" color="warning.main">
                            In Review
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                          This request is currently under review. After examining all details, please approve or decline the request.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <LoadingButton
                            variant="contained"
                            color="success"
                            onClick={() => setOpenApproveDialog(true)}
                            disabled={processing || isSubmitting}
                            loading={processing && processingAction === 'Approving request'}
                            startIcon={<CheckIcon />}
                            sx={{ 
                              borderRadius: 1,
                              boxShadow: '0 4px 10px rgba(46, 125, 50, 0.2)'
                            }}
                          >
                            Approve
                          </LoadingButton>
                          <LoadingButton
                            variant="outlined"
                            color="error"
                            onClick={() => setOpenDeclineDialog(true)}
                            disabled={processing || isSubmitting}
                            loading={processing && processingAction === 'Declining request'}
                            startIcon={<CloseIcon />}
                            sx={{ borderRadius: 1 }}
                          >
                            Decline
                          </LoadingButton>
                        </Box>
                      </Box>
                    )}

                    {currentStatus === 'Approved' && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                          <Typography variant="h6" color="success.main">
                            Approved
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                          This request has been approved and accepted by the member. You can now proceed with disbursing the funds.
                        </Typography>
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          onClick={() => setOpenDisburseDialog(true)}
                          disabled={processing || isSubmitting}
                          loading={processing && processingAction === 'Disbursing funds'}
                          startIcon={<AccountBalanceWalletIcon />}
                          sx={{ 
                            borderRadius: 1,
                            boxShadow: '0 4px 10px rgba(25, 118, 210, 0.2)'
                          }}
                        >
                          Disburse Funds
                        </LoadingButton>
                      </Box>
                    )}

                    {currentStatus === 'MemberRejected' && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <CloseIcon color="error" sx={{ mr: 1 }} />
                          <Typography variant="h6" color="error.main">
                            Rejected by Member
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          The member has rejected this approved request. You may contact them for more information or review the comments below for details.
                        </Typography>
                      </Box>
                    )}

                    {currentStatus === 'Declined' && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <CloseIcon color="error" sx={{ mr: 1 }} />
                          <Typography variant="h6" color="error.main">
                            Declined
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          This request has been declined. The member has been notified of this decision along with the provided reason.
                        </Typography>
                      </Box>
                    )}

                    {currentStatus === 'Disbursed' && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <AttachMoneyIcon color="info" sx={{ mr: 1 }} />
                          <Typography variant="h6" color="info.main">
                            Funds Disbursed
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          Funds have been disbursed to the member. Waiting for the member to confirm receipt of the funds to complete this request.
                        </Typography>
                      </Box>
                    )}

                    {currentStatus === 'Completed' && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <DoneAllIcon color="success" sx={{ mr: 1 }} />
                          <Typography variant="h6" color="success.main">
                            Completed
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          This request has been fully completed. The member has confirmed receipt of the funds and the transaction is now closed.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }}
                >
                  <CardHeader 
                    title="Comments & Updates" 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        fullWidth
                        label="Add a comment"
                        placeholder="Type your comment here..."
                        multiline
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        disabled={isSubmitting}
                        InputProps={{
                          sx: { borderRadius: 1 }
                        }}
                      />
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <LoadingButton
                          variant="contained"
                          disabled={!comment.trim() || isSubmitting}
                          onClick={handleAddComment}
                          loading={isSubmitting}
                          endIcon={<SendIcon />}
                          sx={{ borderRadius: 1 }}
                        >
                          Add Comment
                        </LoadingButton>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box>
                      {request.comments && request.comments.length > 0 ? (
                        <Stack spacing={2} sx={{ maxHeight: 400, overflow: 'auto', pr: 1 }}>
                          {request.comments.map((comment, index) => (
                            <Card 
                              key={index} 
                              variant="outlined" 
                              sx={{
                                borderRadius: 1,
                                borderWidth: 1,
                                borderColor: comment.by === 'admin' 
                                  ? alpha(theme.palette.primary.main, 0.3) 
                                  : alpha(theme.palette.grey[500], 0.3),
                                bgcolor: comment.by === 'admin' 
                                  ? alpha(theme.palette.primary.main, 0.05) 
                                  : alpha(theme.palette.grey[100], 0.7),
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
                                }
                              }}
                            >
                              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar 
                                      sx={{ 
                                        width: 28, 
                                        height: 28, 
                                        fontSize: '0.8rem',
                                        bgcolor: comment.by === 'admin' 
                                          ? theme.palette.primary.main 
                                          : theme.palette.grey[700],
                                        mr: 1
                                      }}
                                    >
                                      {comment.by === 'admin' ? 'A' : 'M'}
                                    </Avatar>
                                    <Typography 
                                      variant="subtitle2" 
                                      color={comment.by === 'admin' ? 'primary.main' : 'text.secondary'}
                                    >
                                      {comment.by === 'admin' ? 'Admin' : 'Member'}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(comment.date)}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ pl: 4 }}>{comment.text}</Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      ) : (
                        <Box sx={{ 
                          p: 3, 
                          textAlign: 'center', 
                          borderRadius: 2, 
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          border: `1px dashed ${theme.palette.divider}`
                        }}>
                          <CommentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No comments yet
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Move to Review Dialog */}
        <Dialog 
          open={openReviewDialog} 
          onClose={() => !processing && setOpenReviewDialog(false)}
          PaperProps={{
            elevation: 5,
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <EditIcon color="primary" />
            Move Request to Review
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <DialogContentText>
              This will mark the request as "In Review" and notify the member.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Additional Comments (optional)"
              fullWidth
              multiline
              rows={3}
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              disabled={isSubmitting || processing}
              InputProps={{
                sx: { borderRadius: 1, mt: 1 }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={() => setOpenReviewDialog(false)} 
              disabled={isSubmitting || processing}
              sx={{ borderRadius: 1 }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleMoveToReview}
              color="primary"
              variant="contained"
              disabled={isSubmitting || processing}
              loading={isSubmitting || processing}
              sx={{ borderRadius: 1 }}
            >
              Move to Review
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Approve Dialog */}
        <Dialog 
          open={openApproveDialog} 
          onClose={() => !processing && setOpenApproveDialog(false)}
          PaperProps={{
            elevation: 5,
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            bgcolor: alpha(theme.palette.success.main, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <CheckCircleIcon color="success" />
            Approve Request
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <DialogContentText>
              This will approve the financial assistance request and notify the member for their acceptance.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Approval Comments (optional)"
              fullWidth
              multiline
              rows={3}
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              disabled={isSubmitting || processing}
              InputProps={{
                sx: { borderRadius: 1, mt: 1 }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={() => setOpenApproveDialog(false)} 
              disabled={isSubmitting || processing}
              sx={{ borderRadius: 1 }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleApproveRequest}
              color="success"
              variant="contained"
              disabled={isSubmitting || processing}
              loading={isSubmitting || processing}
              sx={{ borderRadius: 1 }}
            >
              Approve Request
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Decline Dialog */}
        <Dialog 
          open={openDeclineDialog} 
          onClose={() => !processing && setOpenDeclineDialog(false)}
          PaperProps={{
            elevation: 5,
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            bgcolor: alpha(theme.palette.error.main, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <CloseIcon color="error" />
            Decline Request
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <DialogContentText>
              Please provide a reason for declining this financial assistance request.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Reason for declining"
              fullWidth
              multiline
              rows={3}
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              disabled={isSubmitting || processing}
              required
              error={!actionComment.trim() && openDeclineDialog}
              helperText={!actionComment.trim() && openDeclineDialog ? "A reason is required" : ""}
              InputProps={{
                sx: { borderRadius: 1, mt: 1 }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={() => setOpenDeclineDialog(false)} 
              disabled={isSubmitting || processing}
              sx={{ borderRadius: 1 }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleDeclineRequest}
              color="error"
              variant="contained"
              disabled={isSubmitting || processing || !actionComment.trim()}
              loading={isSubmitting || processing}
              sx={{ borderRadius: 1 }}
            >
              Decline Request
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Disburse Funds Dialog */}
        <Dialog 
          open={openDisburseDialog} 
          onClose={() => !processing && setOpenDisburseDialog(false)}
          PaperProps={{
            elevation: 5,
            sx: { borderRadius: 2 }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <AccountBalanceWalletIcon color="primary" />
            Disburse Funds
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <DialogContentText sx={{ mb: 2 }}>
              Please confirm the amount to be disbursed and provide any additional notes.
            </DialogContentText>
            
            <Box sx={{ 
              p: 2, 
              mb: 2, 
              borderRadius: 1, 
              bgcolor: alpha(theme.palette.success.main, 0.05),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}>
              <Typography variant="subtitle2" color="text.secondary">Requested Amount</Typography>
              <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                ${request.amount > 0 ? request.amount.toFixed(2) : 'Not specified'}
              </Typography>
            </Box>
            
            <TextField
              margin="dense"
              label="Amount to Disburse"
              type="number"
              fullWidth
              value={disburseAmount}
              onChange={(e) => setDisburseAmount(e.target.value)}
              disabled={isSubmitting || processing}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                sx: { borderRadius: 1 }
              }}
              required
              error={!disburseAmount || parseFloat(disburseAmount) <= 0}
              helperText={!disburseAmount || parseFloat(disburseAmount) <= 0 ? "Please enter a valid amount" : ""}
            />
            
            <TextField
              label="Additional Notes (optional)"
              fullWidth
              multiline
              rows={3}
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              disabled={isSubmitting || processing}
              InputProps={{
                sx: { borderRadius: 1 }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={() => setOpenDisburseDialog(false)} 
              disabled={isSubmitting || processing}
              sx={{ borderRadius: 1 }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleDisburseRequest}
              color="primary"
              variant="contained"
              disabled={isSubmitting || processing || !disburseAmount || parseFloat(disburseAmount) <= 0}
              loading={isSubmitting || processing}
              sx={{ borderRadius: 1 }}
            >
              Disburse Funds
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Success notification */}
        <Snackbar
          open={statusUpdateSuccess}
          autoHideDuration={5000}
          onClose={() => setStatusUpdateSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setStatusUpdateSuccess(false)}
            severity="success"
            variant="filled"
            sx={{ width: '100%', borderRadius: 1 }}
            icon={<CheckCircleIcon />}
          >
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Processing backdrop for disbursements */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={processing && processingAction === 'Disbursing funds'}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress color="inherit" size={60} thickness={4} />
            <Typography sx={{ mt: 2, fontWeight: 500 }}>Processing Disbursement...</Typography>
            <Typography variant="caption" sx={{ mt: 1 }}>This may take a few moments</Typography>
          </Box>
        </Backdrop>
      </Box>
    </Container>
  );
};

export default AdminFinancialRequestDetailPage;